/**
 * Bulk settings modal (一括設定) for massager alert and GPS range.
 * Uses React Hook Form + Zod (massagerBulkSettingsFormSchema), AppModal, AppRadioGroup, AppAlert, AppButton, AppTextField.
 */

import {
  AppButton,
  AppCard,
  AppForm,
  AppModal,
  AppRadio,
  AppRadioGroup,
  AppSpace,
  AppTextField
} from '@/components/atoms';
import { useBulkSettingsModalFormGuard } from '@/hooks/main/useMassagerPageFormGuard';
import useAppToast from '@/hooks/useAppToast';
import { MASSAGER_GPS_RANGE_OPTIONS } from '@/pages/main/massager/constants';
import type { BulkUpdateAlertRequest } from '@/pages/main/massager/types';
import {
  massagerBulkSettingsFormSchema,
  type MassagerBulkSettingsFormData,
} from '@/schemas/massagerBulkSettingsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';

export interface MassagerBulkSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BulkUpdateAlertRequest) => Promise<unknown>;
  onSuccess?: (response?: unknown) => void;
  selectedCount: number;
  selectedIds: number[];
  displayType: string;
}

const defaultValues: MassagerBulkSettingsFormData = {
  alertType: null,
  alertDay: '',
  alertMonth: '',
  gpsRange: null,
};

const MassagerBulkSettingsModal: React.FC<MassagerBulkSettingsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSuccess,
  selectedCount,
  selectedIds,
  displayType,
}) => {
  const { error } = useAppToast();
  const { setFormChanged, confirmBeforeAction } = useBulkSettingsModalFormGuard();

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useTrimmedForm<MassagerBulkSettingsFormData>({
    resolver: zodResolver(massagerBulkSettingsFormSchema),
    defaultValues,
    mode: 'all',
  });

  const currentFormData = watch();

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    } else {
      reset(defaultValues);
    }
  }, [isOpen, reset]);

  // Track form changes and report to centralized guard (for router blocker)
  const hasFormChanged = JSON.stringify(currentFormData) !== JSON.stringify(defaultValues);

  useEffect(() => {
    if (isOpen) {
      setFormChanged(hasFormChanged);
    } else {
      setFormChanged(false);
    }
  }, [hasFormChanged, isOpen, setFormChanged]);

  // Local confirmation for close button
  const handleClose = () => {
    if (hasFormChanged) {
      confirmBeforeAction(() => {
        onClose();
      });
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const onFormSubmit = async (data: MassagerBulkSettingsFormData) => {
    const payload: BulkUpdateAlertRequest = { ids: selectedIds };
    if (data.alertType != null) {
      payload.is_consent_alert_off = data.alertType;
      if (data.alertType === 2 && data.alertDay) payload.alert_day = parseInt(data.alertDay, 10);
      if (data.alertType === 3 && data.alertMonth)
        payload.alert_month = parseInt(data.alertMonth, 10);
    }
    if (data.gpsRange != null) payload.is_gps_range = data.gpsRange;

    try {
      const response = await onSubmit(payload);
      onSuccess?.(response);
      setFormChanged(false);
      onClose();
    } catch {
      error('更新に失敗しました。');
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title="一括設定"
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-end gap-3">
          <AppButton
            type="primary"
            onClick={handleSubmit(onFormSubmit)}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? '適用中' : '適用'}
          </AppButton>
        </div>
      }
    >
      <p className="mb-4 text-sm">
        {displayType ? (
          <>
            選択した{displayType}: <span className="font-medium">{selectedCount}</span>件
          </>
        ) : (
          <>
            選択件数: <span className="font-medium">{selectedCount}</span> 件
          </>
        )}
      </p>

      <AppCard title="同意書切れアラート通知" isHeaderColor className="mb-6">
        <Controller
          name="alertType"
          control={control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <AppRadioGroup
                {...field}
                value={field.value ?? undefined}
                onChange={e => {
                  const v = e.target.value as number;
                  field.onChange(v);
                  const opts = { shouldValidate: false };
                  if (v === 1) {
                    setValue('alertDay', '', opts);
                    setValue('alertMonth', '', opts);
                  } else if (v === 2) {
                    setValue('alertMonth', '', opts);
                  } else if (v === 3) {
                    setValue('alertDay', '', opts);
                  }
                  clearErrors(['alertDay', 'alertMonth']);
                }}
                className="flex flex-col gap-2"
              >
                <div className="flex flex-col">
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppRadio value={1}>通知OFF</AppRadio>
                  </AppForm.Item>
                </div>
                <div className="flex flex-col">
                  <Controller
                    name="alertDay"
                    control={control}
                    render={({ field: dayField, fieldState: dayFieldState }) => (
                      <AppForm.Item
                        validateStatus={dayFieldState.error ? 'error' : undefined}
                        help={dayFieldState.error?.message}
                        className="mb-0"
                      >
                        <AppSpace className="items-center flex-wrap">
                          <AppRadio value={2}>
                            <AppTextField
                              {...dayField}
                              onFocus={() => {
                                field.onChange(2);
                                setValue('alertMonth', '', { shouldValidate: false });
                                clearErrors(['alertMonth']);
                              }}
                              placeholder="1～999"
                              className="w-28 text-sm"
                            />
                            <span className="shrink-0 ml-2">日前</span>
                          </AppRadio>
                        </AppSpace>
                      </AppForm.Item>
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <Controller
                    name="alertMonth"
                    control={control}
                    render={({ field: monthField, fieldState: monthFieldState }) => (
                      <AppForm.Item
                        validateStatus={monthFieldState.error ? 'error' : undefined}
                        help={monthFieldState.error?.message}
                        className="mb-0"
                      >
                        <AppSpace className="items-center flex-wrap">
                          <AppRadio value={3}>
                            <AppTextField
                              {...monthField}
                              onFocus={() => {
                                field.onChange(3);
                                setValue('alertDay', '', { shouldValidate: false });
                                clearErrors(['alertDay']);
                              }}
                              placeholder="1～99"
                              className="w-28 text-sm"
                            />
                            <span className="shrink-0 ml-2">ヶ月前</span>
                          </AppRadio>
                        </AppSpace>
                      </AppForm.Item>
                    )}
                  />
                </div>
              </AppRadioGroup>
            </div>
          )}
        />
      </AppCard>

      <Controller
        name="gpsRange"
        control={control}
        render={({ field, fieldState }) => (
          <AppCard title="GPS範囲" isHeaderColor className="mb-6">
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
              className="mb-0"
            >
              <AppRadioGroup
                value={field.value ?? undefined}
                onChange={e => field.onChange(e.target.value as number)}
                className="flex gap-4 flex-wrap"
              >
                {MASSAGER_GPS_RANGE_OPTIONS.map(o => (
                  <AppRadio key={o.value} value={o.value}>
                    {o.label}
                  </AppRadio>
                ))}
              </AppRadioGroup>
            </AppForm.Item>
          </AppCard>
        )}
      />
    </AppModal>
  );
};

export default MassagerBulkSettingsModal;
